import React, { useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';

import Chip from '../../../widgets/Chip';
import Checkbox from '../../../widgets/Checkbox';
import Input from '../../../widgets/Input';
import Button from '../../../widgets/Button';

import { OptionType } from '../../../../shared/types/filters';
import { FilterKeys } from '../../../../shared/constants/filter/filter';
import { useWidgetsModalContext } from '../../../../shared/providers/WidgetsModalProvider';
import useFilters from '../../../../utilities/hooks/useFilters';

import Chevron from '../../../../assets/icons/General/chevron.svg?react';
import FilterIcon from '../../../../assets/icons/filter.svg?react';
import searchIcon from '../../../../assets/icons/General/search.svg';

type ConmmonProps = {
  name: string;
  selectAllItems: (
    isChecked: boolean,
    optionsToSelect?: any,
    isMatching?: boolean,
  ) => void;
  filterKey: string;
  mobileSheet?: any;
  flatItemArray?: any;
};

type ConditionalProps<T> = {
  options: OptionType<T>[];
  isSelected: (value: T) => boolean;
  onSelectedChange: (currentlySelectedItem: T) => void;
  selectedItems: OptionType<T>[];
};

type FilterPanelProps<T> = ConmmonProps & ConditionalProps<T>;

export default function FilterPanel<T>({
  name,
  filterKey,
  mobileSheet,
  options,
  selectedItems,
  onSelectedChange,
  isSelected,
  selectAllItems,
  flatItemArray,
}: FilterPanelProps<T>) {
  const [textFilter, setTextFilter] = useState<string>('');
  const [loadingFilter, setLoadingFilter] = useState(true);
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);
  const [called, setCalled] = useState(true);
  const [toggleTrue, setToggleTrue] = useState(false);
  const [allMatching, setAllMatching] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [numberOfAppliedFilters, setNumberOfAppliedFilters] = useState(0);

  const { t } = useTranslation();
  const {
    setSelectAllChecked,
    selectAllChecked,
    selectedDepartments,
    selectedOrganisations,
    ageFilter,
    genderFilter,
    regionFilter,
  } = useFilters();
  const { showFilterModal, showMobileFilterModal } = useWidgetsModalContext();

  const [optionsToShow, setOptionsToShow] = useState(options);
  const [initialOpenDone, setInitialOpenDone] = useState(false);

  // Function to filter options based on text input
  const filterOptions = (options: any[], textFilter: string): any[] => {
    return options.reduce((result, option) => {
      const match = option.label
        .toLowerCase()
        .includes(textFilter.toLowerCase());

      // Recursively filter children if they exist
      const filteredChildren = option.children
        ? filterOptions(
            option.children,
            option.selectable === false && match ? '' : textFilter,
          )
        : [];

      // If the option or any of its children match, include it
      const shouldIncludeOption = match || filteredChildren.length > 0;

      // Only open parent if any child matches the filter
      const isParentOpen = textFilter
        ? filteredChildren.length > 0
        : option.open;

      if (shouldIncludeOption) {
        result.push({
          ...option,
          children: filteredChildren,
          open: isParentOpen,
        });
      }

      return result;
    }, []);
  };

  const markSelectedAsOpen = (options: any[], selectedValues: any[]): any[] => {
    return options.map((option) => {
      // Recursively process children
      const updatedChildren = option.children
        ? markSelectedAsOpen(option.children, selectedValues)
        : [];

      // Determine open state:
      // - Keep open if already open
      // - Open if any child is selected or already open
      const shouldOpen =
        option.open ||
        updatedChildren.some(
          (child) =>
            child.open ||
            (child.selectable !== false &&
              selectedValues.includes(child.value)),
        );

      return {
        ...option,
        children: updatedChildren,
        open: shouldOpen, // Open only if children are selected or already open
      };
    });
  };

  useEffect(() => {
    if (showFilterModal) {
      // When the modal is visible
      if (selectedItems.length > 0) {
        // Handle selected items
        const selectedValues = selectedItems.map((item) => item.value);
        if (!initialOpenDone) {
          // Mark selected items as open on first load
          if (textFilter.trim() == '') {
            const optionsWithOpen = markSelectedAsOpen(options, selectedValues);
            setOptionsToShow(optionsWithOpen);
          }
          setInitialOpenDone(true);
        }
      } else {
        // When no items are selected
        if (!initialOpenDone)
          setOptionsToShow(options); // Set default options initially
        else {
          // Preserve the current open state of options
          setOptionsToShow((prevOptions) => {
            return prevOptions.map((option) => ({
              ...option,
              children: option.children
                ? option.children.map((child) => ({
                    ...child,
                    open: child.open,
                  }))
                : [],
              open: option.open,
            }));
          });
        }
      }
    } else {
      // Reset options when the modal is closed
      if (initialOpenDone) setOptionsToShow(options);
    }
  }, [showFilterModal, selectedItems, options]);

  useEffect(() => {
    if (initialOpenDone) {
      setOptionsToShow(filterOptions(options, textFilter));
    }
  }, [options]);

  useEffect(() => {
    setOptionsToShow(filterOptions(options, textFilter));
  }, [textFilter]);

  let optionsToSelect =
    textFilter.length > 0
      ? optionsToShow.flatMap((option) => {
          const getChildrenValues = (node: any): string[] => {
            const values: string[] = [];
            if (node.children && node.children.length > 0) {
              for (const child of node.children) {
                values.push(...getChildrenValues(child));
              }
            } else if (node.selectable !== false) {
              values.push(node.value);
            }
            return values;
          };

          return getChildrenValues(option);
        })
      : optionsToShow.flatMap((option) =>
          option.selectable === false
            ? (option.children ?? []).map((child) => child.value)
            : [option.value],
        );

  const deptConditionalCopy =
    filterKey === FilterKeys.department && isSelectAllActive;

  useEffect(() => {
    if (!showMobileFilterModal) setTextFilter('');
  }, [showMobileFilterModal]);

  useEffect(() => {
    if (!mobileSheet) setTextFilter('');
  }, [mobileSheet]);

  useEffect(() => {
    setTimeout(() => {
      setLoadingFilter(false);
    }, 500);
  }, []);

  const handleSelectAllToggle = (options: any) => {
    setToggleTrue(true);
    setCalled(true);
    if (!selectAllChecked?.[filterKey]) {
      selectAllItems(isSelectAllActive, options);
    } else {
      selectAllItems(isSelectAllActive, []);
    }

    setSelectAllChecked({
      ...selectAllChecked,
      [filterKey]: !selectAllChecked?.[filterKey],
    });
  };

  useEffect(() => {
    if (selectAllChecked[filterKey] === undefined) {
      setSelectAllChecked((prevState) => ({
        ...prevState,
        [filterKey]: false,
      }));
      setIsInitialized(true);
    } else setIsInitialized(true);
  }, [filterKey, selectAllChecked, setSelectAllChecked]);

  const countAllElements = (nodes: any) => {
    let totalCount = 0;

    const countRecursive = (node: any) => {
      totalCount++;
      if (node.children && node.children.length > 0) {
        node.children.forEach((child: any) => {
          countRecursive(child);
        });
      }
    };

    nodes.forEach((node: any) => {
      countRecursive(node);
    });

    return totalCount;
  };

  const hasParentsWithChildren = (nodes: any) => {
    for (const node of nodes) {
      if (node.children && node.children.length > 0) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    if (!isInitialized) return;

    const parentHasChildren = hasParentsWithChildren(selectedItems);

    if (parentHasChildren && selectedItems) {
      const itemCount = countAllElements(selectedItems);
      if (itemCount === flatItemArray.length && !isSelectAllActive) {
        setIsSelectAllActive(true);
        setSelectAllChecked((prevState) => ({
          ...prevState,
          [filterKey]: true,
        }));
      } else if (itemCount !== flatItemArray.length && isSelectAllActive) {
        setIsSelectAllActive(false);
        setSelectAllChecked((prevState) => ({
          ...prevState,
          [filterKey]: false,
        }));
      }
    } else {
      const allItemsSelected =
        flatItemArray &&
        selectedItems &&
        flatItemArray.length === selectedItems.length &&
        flatItemArray.length > 0;

      if (allItemsSelected && !isSelectAllActive) {
        setIsSelectAllActive(true);
        setSelectAllChecked((prevState) => ({
          ...prevState,
          [filterKey]: true,
        }));
      } else if (!allItemsSelected && isSelectAllActive) {
        setIsSelectAllActive(false);
        setSelectAllChecked((prevState) => ({
          ...prevState,
          [filterKey]: false,
        }));
      }
    }
  }, [
    flatItemArray,
    selectedItems,
    filterKey,
    isSelectAllActive,
    isInitialized,
  ]);

  useEffect(() => {
    const totalSelected =
      ageFilter.length +
      genderFilter.length +
      regionFilter.length +
      Array.from(selectedDepartments.values()).filter(Boolean).length +
      Array.from(selectedOrganisations.values()).filter(Boolean).length;

    setNumberOfAppliedFilters(totalSelected);
  }, [
    selectedOrganisations,
    selectedDepartments,
    regionFilter,
    genderFilter,
    ageFilter,
  ]);

  useEffect(() => {
    if (numberOfAppliedFilters === 0) {
      setSelectAllChecked({});
    }
  }, [numberOfAppliedFilters]);

  useEffect(() => {
    if (textFilter) {
      const selectedValues = selectedItems.map((item: any) => item.value);
      const allPresent = (optionsToSelect as any[]).every((option) =>
        selectedValues.includes(option),
      );
      setAllMatching(!allPresent);
    }
  }, [selectedItems, optionsToSelect, optionsToShow]);

  return (
    <div className="flex flex-col">
      <div
        className={`group-[.org-dropdown]:!grid-cols-1 lg:grid lg:grid-cols-2 lg:[&>*]:h-[65vh] ${
          !loadingFilter && '[&>*]:overflow-y-auto'
        }`}
      >
        <div className="border-black-200 relative border-r">
          {(filterKey == FilterKeys.region ||
            filterKey == FilterKeys.department) &&
            loadingFilter && (
              <div className="bg-black-200 absolute top-0 z-[99] h-full w-full animate-pulse" />
            )}

          <div
            className={`border-black-200 sticky top-0 z-10 border-b bg-white lg:pt-5 ${
              (filterKey == FilterKeys.region ||
                filterKey == FilterKeys.department) &&
              loadingFilter &&
              'opacity-0'
            }`}
          >
            {!showMobileFilterModal && (
              <div className="flex items-center justify-between px-6 pt-[30px] pb-6 lg:px-5 lg:py-2">
                <p
                  className={`text-grey-800 text-lg font-semibold md:text-sm md:font-medium ${
                    showFilterModal && '!text-grey-800 lg:!text-sm'
                  }`}
                >
                  {showFilterModal ? t('SEARCH') : t('SELECT')} {name}
                </p>
              </div>
            )}

            <div className="relative mx-[16px] mb-2">
              <img
                src={searchIcon}
                className="absolute top-[14px] left-[14px] z-10 lg:top-[12px]"
                alt=""
              />

              <Input
                name={name}
                id={`${name}-text-filter`}
                value={textFilter}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setTextFilter(e.target.value);
                }}
                placeholder={t('filterSearch') ?? 'n/a'}
                inputClass="w-full md:!w-4/5 pl-10 shadow-[0px_1px_2px_0px_#1018280] lg:h-10"
              />
            </div>

            <div className="px-[6px] py-[2px]">
              {!textFilter && (
                <Checkbox
                  checked={selectAllChecked?.[filterKey]}
                  name={`${filterKey}-text-filter`}
                  onChange={() => handleSelectAllToggle(optionsToSelect)}
                  label={t('filterSelectAll')}
                  parentClass="!px-[10px]"
                />
              )}

              {textFilter && (
                <Checkbox
                  checked={!allMatching}
                  name={`${filterKey}-text-filter-match`}
                  onChange={() => {
                    selectAllItems(!allMatching, optionsToSelect, true);
                    // setTimeout(() => {
                    setAllMatching(!allMatching);
                    // }, 200);
                  }}
                  label={t('SELECT_ALL_MATCHING')}
                  parentClass=" !px-[10px]"
                />
              )}
            </div>
            <div className="flex gap-5"></div>
          </div>

          <div
            className={`${
              (filterKey == FilterKeys.region ||
                filterKey == FilterKeys.department) &&
              loadingFilter &&
              'opacity-0'
            }`}
          >
            {optionsToShow.length === 0 ? (
              <div className="mt-[9px] pl-4 text-sm text-[#636C73]">
                {t('filterNoOptionsFound')}
              </div>
            ) : (
              optionsToShow.map((option) => (
                <OptionItem<T>
                  disabled={false}
                  key={`${name}-filter-panel-option-${option.label}-${option.value}`}
                  option={option}
                  onSelectedChange={(e) => {
                    onSelectedChange(e);
                    setCalled(false);
                  }}
                  isSelected={isSelected}
                />
              ))
            )}
          </div>
        </div>

        {!showMobileFilterModal && (
          <div
            className={`relative py-3 pt-5 group-[.org-dropdown]:hidden ${
              deptConditionalCopy &&
              '3xl:!h-[65vh] pb-[60px] lg:!h-[48vh] xl:!h-[52vh] 2xl:!h-[56vh]'
            }`}
          >
            {selectedItems.length !== 0 && (
              <div
                className="text-black-600 absolute top-5 z-10 w-full cursor-pointer px-4 pb-[14px] text-sm font-semibold"
                onClick={() => selectAllItems(true)}
              >
                <span className="mr-2 font-medium text-blue-700 underline">
                  {t('filterReset')} {name} {t('filter')}
                </span>
                <span className="relative rounded-full bg-blue-700 px-2 py-0.5 text-xs text-white">
                  {selectedItems.length !== 0 && `${selectedItems.length}`}
                </span>
              </div>
            )}
            <div
              className={`relative flex flex-wrap content-start items-start gap-2 px-4 pt-[34px] ${
                selectedItems.length == 0 && 'h-full'
              }`}
            >
              {selectedItems.length > 0 ? (
                selectedItems.map((item) => {
                  const modifiedLabel =
                    item.value === 'NA'
                      ? t(item.label.replace(' ' + String(t('YEARS')), ''))
                      : item.label;

                  return (
                    <Chip
                      label={modifiedLabel}
                      key={`selected-chip-${modifiedLabel}-${item.value}`}
                      handleClick={() => onSelectedChange(item.value)}
                    />
                  );
                })
              ) : (
                <div className="relative grid h-full w-full content-center justify-center overflow-hidden">
                  <div className="-mt-20">
                    <FilterIcon
                      className="m-auto mb-9 h-9 w-9"
                      stroke="var(--color-black-500)"
                    />
                    <p className="text-black-500 text-sm">
                      {t('FILTERS_ARE_SHOWN_HERE')}
                    </p>
                  </div>
                </div>
              )}

              {deptConditionalCopy && (
                <div className="text-black-500 fixed bottom-14 bg-white py-2 pr-4 text-sm">
                  Note: Totals may not add up in the adoption section, as some
                  users might have skipped department or region selection.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function OptionItem<T>({
  option,
  isSelected,
  onSelectedChange,
  disabled,
}: {
  option: OptionType<T>;
  isSelected: (value: T) => boolean;
  onSelectedChange: (currentlySelectedItem: T) => void;
  disabled: boolean;
}) {
  const [isOpen, setIsOpen] = useState<boolean>(option.open ?? false);

  useEffect(() => {
    setIsOpen(option.open ?? false);
  }, [option]);

  if (option.selectable === false) {
    return (
      <div>
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen(!isOpen)}
          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold"
        >
          {option.label}
          <Chevron
            stroke="var(--color-black-500)"
            className={`transform transition-all duration-75 ${isOpen ? '-rotate-180' : ''}`}
          />
        </button>
        {isOpen && (
          <div className="flex flex-col pl-4">
            {option.children?.map((child) => (
              <OptionItem<T>
                key={`${child.label}-${child.value}`}
                option={child}
                disabled={disabled}
                isSelected={isSelected}
                onSelectedChange={onSelectedChange}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (option.children?.length) {
    return (
      <div>
        <div className="flex items-center justify-between px-[6px] py-[2px]">
          <Checkbox
            disabled={disabled}
            label={option.label}
            name={`${option.label}-${option.value}`}
            checked={isSelected(option.value) || disabled}
            onChange={() => onSelectedChange(option.value)}
            parentClass="px-[10px] w-full"
            labelClass="font-semibold text-block-700"
          />
          <Button
            type="button"
            variant="ghost"
            onClick={() => setIsOpen(!isOpen)}
            className="!py-[10px]"
          >
            <Chevron
              stroke="var(--color-black-500)"
              className={`transform transition-all duration-75 ${
                isOpen ? '-rotate-180' : ''
              }`}
            />
            <span className="sr-only">Expand the {option.label} option.</span>
          </Button>
        </div>
        <Transition
          show={isOpen}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <div className="flex flex-col pl-4">
            {option.children.map((child) => (
              <OptionItem<T>
                disabled={disabled || isSelected(option.value)}
                key={`${option.label}-filter-panel-option-${child.label}-${child.value}`}
                option={child}
                isSelected={isSelected}
                onSelectedChange={onSelectedChange}
              />
            ))}
          </div>
        </Transition>
      </div>
    );
  } else {
    return (
      <div className="px-[6px] py-[2px]">
        <Checkbox
          disabled={disabled}
          name={`${option.label}-${option.value}`}
          label={option.label}
          checked={isSelected(option.value) || disabled}
          onChange={() => onSelectedChange(option.value)}
          parentClass="px-[10px]"
        />
      </div>
    );
  }
}
